import { FramingProduct } from '../../../components/Checkout/Framing/types';
import { buildFrame } from '../../../components/Checkout/Framing/utils/framePricing';
import { Frame, FrameOptionConsolidated } from '../../../types/Products';
import { shopifyGraphqlIdToAdminId } from '../../../utils/shopifyIds';
import { CHECKOUT_FRAME_PARAM, CHECKOUT_UNFRAMED } from '../constants';

type FrameQuery = Partial<Record<string, string | string[]>>;

const firstQueryValue = (value: string | string[] | undefined) => {
  return Array.isArray(value) ? value[0] : value;
};

const frameVariantIds = (
  variant: FrameOptionConsolidated['variants'][number],
) => {
  return variant.consolidatedVariants.map((consolidatedVariant) => {
    return shopifyGraphqlIdToAdminId(consolidatedVariant.shopifyFrameVariantId);
  });
};

const frameVariantAdminId = (
  frame: Frame | null | undefined,
): string | null => {
  const variantId =
    frame?.variant.consolidatedVariants?.[0]?.shopifyFrameVariantId;

  return variantId ? shopifyGraphqlIdToAdminId(variantId) : null;
};

export const buildFrameQuery = (frame: Frame | null | undefined): string => {
  const variantId = frameVariantAdminId(frame);

  if (!variantId) {
    return '';
  }

  return new URLSearchParams({ [CHECKOUT_FRAME_PARAM]: variantId }).toString();
};

export const withFrameQuery = (
  link: string,
  frame: Frame | null | undefined,
): string => {
  const [pathname, search = ''] = link.split('?');
  const params = new URLSearchParams(search);
  const variantId = frameVariantAdminId(frame);

  params.delete(CHECKOUT_FRAME_PARAM);
  if (variantId) {
    params.set(CHECKOUT_FRAME_PARAM, variantId);
  }

  const query = params.toString();

  return query ? `${pathname}?${query}` : pathname;
};

export const framingChoiceValue = (frame: Frame | null): string | null => {
  return frame ? frameVariantAdminId(frame) : CHECKOUT_UNFRAMED;
};

/**
 * A checkout link with the page's work already framed as chosen, or left
 * unframed (`frame=none`).
 */
export const withFramingChoice = (
  link: string,
  frame: Frame | null,
): string => {
  const [pathname, search = ''] = link.split('?');
  const params = new URLSearchParams(search);
  const value = framingChoiceValue(frame);

  params.delete(CHECKOUT_FRAME_PARAM);
  if (value) params.set(CHECKOUT_FRAME_PARAM, value);

  return `${pathname}?${params.toString()}`;
};

export const parseFrameVariantIdQuery = (query: FrameQuery): string | null => {
  return firstQueryValue(query[CHECKOUT_FRAME_PARAM]) || null;
};

export const resolveFrameByVariantId = (
  product: Pick<FramingProduct, 'frameOptions'>,
  variantId: string,
): Frame | null => resolveFrameFromOptions(product.frameOptions, variantId);

/** The frame among `frameOptions` whose variant has this admin id. */
export const resolveFrameFromOptions = (
  frameOptions: FrameOptionConsolidated[],
  variantId: string,
): Frame | null => {
  for (const option of frameOptions) {
    const variant = option.variants.find((candidate) => {
      return frameVariantIds(candidate).includes(variantId);
    });

    if (variant) {
      return buildFrame(option, variant.title);
    }
  }

  return null;
};

/**
 * The frame a page opens with from its `frame` query param: the matching
 * option, `null` for `frame=none`, or `undefined` when there is no param or it
 * matches nothing, so the page keeps its own default.
 */
export const resolveFrameQuery = (
  frameOptions: FrameOptionConsolidated[],
  variantId: string | null,
): Frame | null | undefined => {
  if (!variantId) return undefined;
  if (variantId === CHECKOUT_UNFRAMED) return null;

  return resolveFrameByVariantId({ frameOptions }, variantId) ?? undefined;
};
